import { supabase, unwrap } from '../supabase';
import { phoneQueryDigits } from '../phone';
import {
  clockAt,
  normalizeTime,
  overdueGroup,
  storedTypes,
  tabStatus,
  tabType,
  type Clock,
  type TaskStatus,
  type TaskTab,
  type TaskType,
} from '../tasks';
import { safeTerm } from './calls';
import { wakeDevice } from './employees';
import type { Paged, Task, TaskCounts } from '../types';

// Tasks live in crm_tasks, shared with the CallSync app (see backend-cloud/app-api/README.md,
// "Tasks"). The owner reads and writes them directly under the owner_access policy. A trigger
// moves updated_at on every write and records tasks that leave a phone, so an edit, a
// reassignment or a delete reaches the employee's phone on its next task sync.

export interface TaskQuery {
  tab: TaskTab;
  /** An employee id; the Not assigned tab ignores it. */
  employeeId?: string;
  search?: string;
  /** Tasks linked to this number (the contact panel). */
  phone?: string;
}

export interface TaskInput {
  type: TaskType;
  description: string | null;
  assignedEmployeeId: number | null;
  contactPhone: string | null;
  contactName: string | null;
  contactLabel: string | null;
  dueDate: string | null;
  dueTime: string | null;
  showReminder: boolean;
  reminderMinutesBefore: number;
}

/** The task changed (usually on the employee's phone) after it was opened; `task` is the latest. */
export class TaskChangedError extends Error {
  task: Task | null;
  constructor(task: Task | null) {
    super(
      task
        ? 'This task changed after you opened it. Check the latest details and make your change again.'
        : 'This task was deleted.',
    );
    this.task = task;
  }
}

const COLUMNS =
  'id,client_id,type,description,person_id,contact_phone,contact_name,contact_label,due_date,due_time,' +
  'show_reminder,reminder_minutes_before,status,completed_at,assigned_employee_id,created_by,' +
  'created_by_employee_id,created_at,updated_at';

const toStr = (v: unknown) => (v === null || v === undefined || v === '' ? null : String(v));
const toId = (v: unknown) => (v === null || v === undefined || v === '' ? null : Number(v));

type People = Map<number, { name: string; archived: boolean }>;

export function mapTask(row: Record<string, unknown>, people: People = new Map()): Task {
  const assigned = toId(row.assigned_employee_id);
  const creator = toId(row.created_by_employee_id);
  return {
    id: Number(row.id),
    clientId: String(row.client_id ?? ''),
    type: (toStr(row.type) ?? 'OTHER') as TaskType,
    description: toStr(row.description),
    contactPhone: toStr(row.contact_phone),
    contactName: toStr(row.contact_name),
    contactLabel: toStr(row.contact_label),
    personId: toId(row.person_id),
    dueDate: toStr(row.due_date)?.slice(0, 10) ?? null,
    dueTime: normalizeTime(row.due_time),
    showReminder: row.show_reminder === true,
    reminderMinutesBefore: Number(row.reminder_minutes_before) || 0,
    status: (toStr(row.status) ?? 'PENDING') as TaskStatus,
    completedAt: toStr(row.completed_at),
    assignedEmployeeId: assigned,
    assignedEmployeeName: assigned === null ? null : (people.get(assigned)?.name ?? null),
    assignedEmployeeArchived: assigned !== null && !!people.get(assigned)?.archived,
    createdBy: row.created_by === 'employee' ? 'employee' : 'owner',
    createdByEmployeeId: creator,
    createdByEmployeeName: creator === null ? null : (people.get(creator)?.name ?? null),
    createdAt: toStr(row.created_at),
    updatedAt: String(row.updated_at ?? ''),
  };
}

type Builder = ReturnType<ReturnType<typeof supabase.from>['select']>;

/** The filters, then the tab (the tab counts share everything else). */
function applyQuery(query: Builder, orgId: string, q: Omit<TaskQuery, 'tab'>, tab: TaskTab, clock: Clock): Builder {
  const groups: string[] = [];
  let b = query.eq('org_id', orgId);
  if (tab === 'unassigned') b = b.is('assigned_employee_id', null);
  else if (q.employeeId && /^\d+$/.test(q.employeeId)) b = b.eq('assigned_employee_id', Number(q.employeeId));
  if (q.phone) b = b.eq('contact_phone', q.phone);
  const search = q.search?.trim();
  if (search) {
    const digits = phoneQueryDigits(search);
    if (digits) b = b.ilike('contact_phone', `%${digits}%`);
    else {
      const term = safeTerm(search);
      if (term) groups.push(`or(description.ilike.%${term}%,contact_name.ilike.%${term}%)`);
    }
  }
  const status = tabStatus(tab);
  if (status) b = b.eq('status', status);
  // Today and Upcoming follow the app: today's tasks include ones whose time has passed.
  if (tab === 'today') b = b.eq('due_date', clock.today);
  if (tab === 'upcoming') b = b.gt('due_date', clock.today);
  if (tab === 'nodate') b = b.is('due_date', null);
  if (tab === 'overdue') groups.push(overdueGroup(clock));
  const type = tabType(tab);
  if (type) b = b.in('type', storedTypes(type));

  if (groups.length === 1) b = b.or(groups[0].replace(/^or\((.*)\)$/s, '$1'));
  else if (groups.length > 1) b = b.or(`and(${groups.join(',')})`);
  return b;
}

/** Names for the employees a page of tasks mentions, archived ones included. */
async function peopleFor(orgId: string, rows: Record<string, unknown>[]): Promise<People> {
  const ids = [
    ...new Set(
      rows.flatMap((row) => [toId(row.assigned_employee_id), toId(row.created_by_employee_id)]),
    ),
  ].filter((id): id is number => id !== null);
  if (!ids.length) return new Map();
  const people = unwrap(
    await supabase.from('employees').select('id,name,is_archived').eq('org_id', orgId).in('id', ids),
  ) as { id: number; name: string | null; is_archived: boolean | null }[] | null;
  return new Map(
    (people ?? []).map((p) => [Number(p.id), { name: p.name || 'Unnamed', archived: !!p.is_archived }]),
  );
}

/** A contact renamed since the task was saved shows its current name. */
async function currentNames(orgId: string, rows: Record<string, unknown>[]) {
  const phones = [...new Set(rows.map((row) => toStr(row.contact_phone)).filter((p): p is string => !!p))];
  if (!phones.length) return rows;
  const found = unwrap(
    await supabase.from('identities').select('phone,name').eq('org_id', orgId).in('phone', phones),
  ) as { phone: string; name: string | null }[] | null;
  const names = new Map((found ?? []).filter((i) => i.name?.trim()).map((i) => [i.phone, i.name!.trim()]));
  return rows.map((row) => {
    const name = names.get(String(row.contact_phone));
    return name ? { ...row, contact_name: name } : row;
  });
}

async function mapRows(orgId: string, rows: Record<string, unknown>[]): Promise<Task[]> {
  const [people, named] = await Promise.all([peopleFor(orgId, rows), currentNames(orgId, rows)]);
  return named.map((row) => mapTask(row, people));
}

export async function listTasks(
  orgId: string,
  q: TaskQuery,
  page: number,
  pageSize: number,
  signal?: AbortSignal,
): Promise<Paged<Task>> {
  const clock = clockAt();
  const from = (page - 1) * pageSize;
  let b = applyQuery(supabase.from('v_crm_tasks').select(COLUMNS, { count: 'exact' }), orgId, q, q.tab, clock);
  // Open tasks soonest first, undated last; finished ones newest first.
  if (tabStatus(q.tab) === 'PENDING')
    b = b
      .order('due_date', { ascending: true, nullsFirst: false })
      .order('due_time', { ascending: true, nullsFirst: false })
      .order('created_at', { ascending: true });
  else if (q.tab === 'completed') b = b.order('completed_at', { ascending: false, nullsFirst: false });
  else b = b.order('updated_at', { ascending: false });
  const query = b.order('id', { ascending: false }).range(from, from + pageSize - 1);
  const res = await (signal ? query.abortSignal(signal) : query);
  const rows = (unwrap(res) as Record<string, unknown>[] | null) ?? [];
  return { rows: await mapRows(orgId, rows), total: res.count ?? rows.length };
}

export async function taskCounts(orgId: string, q: Omit<TaskQuery, 'tab'>, tabs: TaskTab[]): Promise<TaskCounts> {
  const clock = clockAt();
  const count = async (tab: TaskTab) => {
    const res = await applyQuery(
      supabase.from('v_crm_tasks').select('id', { count: 'exact', head: true }),
      orgId,
      q,
      tab,
      clock,
    );
    unwrap(res);
    return res.count ?? 0;
  };
  const counts = await Promise.all(tabs.map(count));
  return Object.fromEntries(tabs.map((tab, i) => [tab, counts[i]]));
}

export async function getTask(orgId: string, id: number): Promise<Task | null> {
  const row = unwrap(
    await supabase.from('v_crm_tasks').select(COLUMNS).eq('org_id', orgId).eq('id', id).maybeSingle(),
  ) as Record<string, unknown> | null;
  return row ? (await mapRows(orgId, [row]))[0] : null;
}

const text = (value: string | null | undefined, max: number) => value?.trim().slice(0, max) || null;

function toRow(input: Partial<TaskInput>): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  if (input.type !== undefined) row.type = input.type;
  if (input.description !== undefined) row.description = text(input.description, 5000);
  if (input.assignedEmployeeId !== undefined) row.assigned_employee_id = input.assignedEmployeeId;
  if (input.contactPhone !== undefined) row.contact_phone = text(input.contactPhone, 50);
  if (input.contactName !== undefined) row.contact_name = text(input.contactName, 200);
  if (input.contactLabel !== undefined) row.contact_label = text(input.contactLabel, 200);
  if (input.dueDate !== undefined) row.due_date = input.dueDate || null;
  if (input.dueTime !== undefined) row.due_time = normalizeTime(input.dueTime);
  if (input.showReminder !== undefined) row.show_reminder = input.showReminder;
  if (input.reminderMinutesBefore !== undefined) row.reminder_minutes_before = input.reminderMinutesBefore;
  return row;
}

/**
 * Ask the phones a change concerns to pull now (the Team page's Sync push). Without it the
 * phone still gets the change on its next task sync, so a failed push is not an error.
 */
function wake(...ids: (number | null | undefined)[]) {
  for (const id of new Set(ids)) {
    if (typeof id === 'number') void wakeDevice(id).catch(() => undefined);
  }
}

export async function createTask(orgId: string, input: TaskInput): Promise<Task> {
  // client_id, created_by ('owner') and updated_at keep their database defaults.
  const row = unwrap(
    await supabase
      .from('crm_tasks')
      .insert({ ...toRow(input), org_id: orgId, status: 'PENDING' })
      .select(COLUMNS)
      .single(),
  ) as Record<string, unknown>;
  wake(input.assignedEmployeeId);
  return (await mapRows(orgId, [row]))[0];
}

/** A Sales or MCP task has a CRM contact but no number; the phone links tasks by number. */
async function crmPhone(orgId: string, personId: number): Promise<string | null> {
  const row = unwrap(
    await supabase.from('crm_view').select('display_phone').eq('org_id', orgId).eq('id', personId).maybeSingle(),
  ) as { display_phone?: string | null } | null;
  return toStr(row?.display_phone);
}

/**
 * Saves only the changed fields, and only if nobody changed the task since `task` was read
 * (the phone follows the same rule). Otherwise throws TaskChangedError with the latest task.
 */
async function guardedUpdate(orgId: string, task: Task, values: Record<string, unknown>): Promise<Task> {
  const rows = unwrap(
    await supabase
      .from('crm_tasks')
      .update(values)
      .eq('org_id', orgId)
      .eq('id', task.id)
      .eq('updated_at', task.updatedAt)
      .select(COLUMNS),
  ) as Record<string, unknown>[] | null;
  if (rows?.length) return (await mapRows(orgId, rows))[0];
  throw new TaskChangedError(await getTask(orgId, task.id));
}

export async function updateTask(orgId: string, task: Task, patch: Partial<TaskInput>): Promise<Task> {
  const values = toRow(patch);
  if (!Object.keys(values).length) return task;
  if (patch.assignedEmployeeId && !task.contactPhone && patch.contactPhone === undefined && task.personId) {
    const phone = await crmPhone(orgId, task.personId);
    if (phone) values.contact_phone = phone;
  }
  const saved = await guardedUpdate(orgId, task, values);
  wake(saved.assignedEmployeeId, task.assignedEmployeeId);
  return saved;
}

/**
 * One field changed from the table. If the phone changed other fields meanwhile, the change goes
 * ahead on the latest task; if the phone changed this field too, the phone's value stays.
 */
export async function updateTaskField(orgId: string, task: Task, patch: Partial<TaskInput>): Promise<Task> {
  try {
    return await updateTask(orgId, task, patch);
  } catch (error) {
    const latest = error instanceof TaskChangedError ? error.task : null;
    const fields = Object.keys(patch) as (keyof TaskInput)[];
    if (latest && fields.every((key) => latest[key] === task[key])) return updateTask(orgId, latest, patch);
    throw error;
  }
}

/** Done or open again. A task the phone already finished counts as done. */
export async function markTaskDone(orgId: string, task: Task, done: boolean): Promise<Task> {
  const status: TaskStatus = done ? 'COMPLETED' : 'PENDING';
  try {
    const saved = await guardedUpdate(orgId, task, {
      status,
      completed_at: done ? new Date().toISOString() : null,
    });
    wake(saved.assignedEmployeeId);
    return saved;
  } catch (error) {
    if (error instanceof TaskChangedError && error.task?.status === status) return error.task;
    throw error;
  }
}

/** Gone for good; the employee's phone drops it on its next task sync. */
export async function deleteTask(orgId: string, task: Task): Promise<void> {
  unwrap(await supabase.from('crm_tasks').delete().eq('org_id', orgId).eq('id', task.id));
  wake(task.assignedEmployeeId);
}
